// GENERAL
import { Box, Typography } from "@mui/material";
import { tokens } from "../../theme";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../../AuthContext";

// ICON
import SchoolIcon from "@mui/icons-material/School";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import PsychologyIcon from "@mui/icons-material/Psychology";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChildCareIcon from "@mui/icons-material/ChildCare";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import Diversity3Icon from "@mui/icons-material/Diversity3";

// COMPONENTS
import LineChart from "../../components/LineChart";
import GeographyChart from "../../components/GeographyChart";
import TargetMin from "../../components/targets/TargetMin";
import PageGuard from "../../components/PageGuard";
import ReportButton from "../../components/reports/ReportButton";
import LoadingState from "../../components/LoadingState";

// UTILS
import { useRedfidViews } from "../../utils/redfidView";
import { DASHBOARD_ROUTES } from "../../utils/mapers";
import { getCount, universityImage } from "../../utils/utils";

function Clickable({ to, children, sx }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        width: "100%",
        cursor: "pointer",
        transition: "transform 120ms ease",
        "&:hover": { transform: "translateY(-1px)" },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

const Dashboard = () => {
  const auth = useAuth();
  const colors = tokens();
  const { loading, error, data } = useRedfidViews();
  const detalleUsuario = data?.index?.userDetailById?.get(String(auth?.user?.user_id));

  const loggedUser = data?.raw.users?.find(
    (u) => String(u.id) === String(auth?.user?.user_id)
  );

  const canSeeReportButton =
    loggedUser?.is_formador || loggedUser?.is_admin || loggedUser?.is_staff;

  const canSeeLevels = Boolean(
    loggedUser?.is_admin || loggedUser?.is_staff
  );

  if (loading) return <LoadingState message="" />;
  if (error) return <div>Error cargando datos: {String(error)}</div>;

  const showMap = data?.country === "chile";
  const total = data.dash.total_formadores;
  const freqNivel = data.dash.by_level;
  const freqGrado = data.dash.by_degree;
  const freqTitulo = data.dash.by_title;
  const freqRegion = data.dash.by_region_code
  const growth_by_year = data.dash.growth_by_year
  const dataForLine = growth_by_year.map(d => ({
    id: String(d.year),
    value: d.total_acumulado,
  }));
  const programa = data.formacion.programas;
  const universidades = data.tables.universidades
  return (
    <PageGuard
      title="PANEL DEL DATOS"
      subtitle="Resumen general de formadores">
      <Box
        m="20px"
        ml="5px"
        sx={{ minHeight: "100vh" }}
        pb="100px">
        {canSeeReportButton && (
          <Box display="flex" justifyContent="flex-start">
            <ReportButton persona={detalleUsuario} data={data} />
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: showMap ? "minmax(0, 1fr) 360px" : "1fr",
            },
            gap: {
              xs: "10px",
              sm: "12px",
              md: "20px"
            },
            alignItems: "start",
          }}
        >
          {/* ==========================
            COLUMNA IZQUIERDA 
           ========================== */}
          <Box
            sx={{
              minWidth: 0,
              display: "grid",
              gridTemplateColumns: "repeat(12, 1fr)",
              gridAutoRows: "auto",
              gap: {
                xs: "10px",
                sm: "12px",
                md: "20px"
              },
              alignContent: "start",
            }}
          >
            {/* ==========================
            FORMADORES | NIVELES | GRADO
           ========================== */}
            {/* FORMADORES */}
            <Box
              sx={{
                gridColumn: {
                  xs: "span 12",
                  md: "span 4"
                },
                gridRow: "span 2",
                backgroundColor: colors.primary[200],
                pt: "15px",
                pr: "15px",
                pl: "0px",
                pb: "15px",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: 42,
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5,
                }}>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  color={colors.primary[100]}
                  sx={{
                    lineHeight: 1,
                    m: 0,
                    fontFamily: "inherit"
                  }}
                >
                  FORMADORES
                </Typography>
              </Box>
              <Box
                flex="1"
                display="flex"
                alignItems="flex-start">
                <Clickable to={DASHBOARD_ROUTES.participantes}>
                  <TargetMin
                    icon={<GroupsIcon sx={{ color: colors.green[200] }} />}
                    value={total}
                    title="Total"
                    titlePosition="bottom"
                    duration={1500}
                    orientation="vertical"
                    variant="hero"
                    fullWidth
                    sx={{
                      width: "100%",
                      maxWidth: "100%",
                      minHeight: {
                        xs: 220,
                        sm: 230,
                        md: 260,
                        lg: 280
                      },
                    }}
                  />
                </Clickable>
              </Box>
            </Box>
            {/* NIVELES */}
            {canSeeLevels && (
              <Box
                sx={{
                  gridColumn: {
                    xs: "span 12",
                    md: "span 4"
                  },
                  gridRow: "span 2",
                  backgroundColor: colors.primary[200],
                  pt: "15px",
                  pr: "15px",
                  pl: "0px",
                  pb: "15px",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    height: 42,
                    display: "flex",
                    alignItems: "center",
                    mb: 1.5
                  }}>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={colors.primary[100]}
                    sx={{
                      lineHeight: 1,
                      m: 0,
                      fontFamily: "inherit"
                    }}
                  >
                    AÑOS DE EXPERIENCIA
                  </Typography>
                </Box>
                <Box
                  display="grid"
                  gridTemplateColumns="repeat(1, 1fr)"
                  gap={{
                    xs: "10px",
                    md: "14px"
                  }}>
                  <Clickable to={DASHBOARD_ROUTES.formacion_niveles}>
                    <TargetMin
                      icon={<PsychologyIcon sx={{ color: colors.green[200] }} />}
                      value={freqNivel.find((c) => c.id === "novel")?.value || 0}
                      title="Novel"
                      titlePosition="bottom"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                    />
                  </Clickable>
                  <Clickable to={DASHBOARD_ROUTES.formacion_niveles}>
                    <TargetMin
                      icon={<PsychologyIcon sx={{ color: colors.green[200] }} />}
                      value={freqNivel.find((c) => c.id === "intermedio")?.value || 0}
                      title="Intermedio"
                      titlePosition="bottom"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                    />
                  </Clickable>
                  <Clickable to={DASHBOARD_ROUTES.formacion_niveles}>
                    <TargetMin
                      icon={<PsychologyIcon sx={{ color: colors.green[200] }} />}
                      value={freqNivel.find((c) => c.id === "experto")?.value || 0}
                      title="Experto"
                      titlePosition="bottom"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                    />
                  </Clickable>
                  <Clickable to={DASHBOARD_ROUTES.formacion_niveles}>
                    <TargetMin
                      icon={<PsychologyIcon sx={{ color: colors.green[200] }} />}
                      value={freqNivel.find((c) => c.id === "—")?.value || 0}
                      title="Sin Información"
                      titlePosition="bottom"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                    />
                  </Clickable>
                </Box>
              </Box>
            )}
            {/* GRADO */}
            <Box
              sx={{
                gridColumn: {
                  xs: "span 12",
                  md: canSeeLevels ? "span 4" : "span 8"
                },
                gridRow: "span 2",
                backgroundColor: colors.primary[200],
                pt: "15px",
                pr: "15px",
                pl: "0px",
                pb: "15px",
                display: "flex",
                flexDirection: "column",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: 42,
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5
                }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={colors.primary[100]}
                  sx={{
                    lineHeight: 1,
                    m: 0,
                    fontFamily: "inherit"
                  }}
                >
                  GRADO ACADÉMICO
                </Typography>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(1, 1fr)",
                  md: canSeeLevels ? "repeat(1, 1fr)" : "repeat(2, 1fr)"
                }}
                gap={{
                  xs: "10px",
                  md: "14px"
                }}>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_grado}>
                  <TargetMin
                    icon={<HistoryEduIcon sx={{ color: colors.green[200] }} />}
                    value={freqGrado.find((c) => c.id === "licenciatura")?.value || 0}
                    title="Licenciatura"
                    titlePosition="bottom"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_grado}>
                  <TargetMin
                    icon={<HistoryEduIcon sx={{ color: colors.green[200] }} />}
                    value={freqGrado.find((c) => c.id === "magister")?.value || 0}
                    title="Magíster"
                    titlePosition="bottom"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_grado}>
                  <TargetMin
                    icon={<HistoryEduIcon sx={{ color: colors.green[200] }} />}
                    value={freqGrado.find((c) => c.id === "doctorado")?.value || 0}
                    title="Doctorado"
                    titlePosition="bottom"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_niveles}>
                  <TargetMin
                    icon={<PsychologyIcon sx={{ color: colors.green[200] }} />}
                    value={freqGrado.find((c) => c.id === "—")?.value || 0}
                    title="Sin Información"
                    titlePosition="bottom"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
              </Box>
            </Box>

            {/* ==========================
              TÍTULO PROFESIONAL + PROGRAMAS
           ========================== */}
            <Box
              sx={{
                gridColumn: "span 12",
                backgroundColor: colors.primary[200],
                pt: "15px",
                pr: "15px",
                pl: "0px",
                pb: "15px",
                display: "flex",
                flexDirection: "column",
                gap: {
                  xs: 1.2,
                  md: 1.5
                },
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: 42,
                  display: "flex",
                  alignItems: "center"
                }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={colors.primary[100]}
                  sx={{
                    m: 0,
                    fontFamily: "inherit"
                  }}>
                  TÍTULO PROFESIONAL
                </Typography>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "repeat(1, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(4, minmax(0, 1fr))",
                }}
                gap={{
                  xs: "10px",
                  md: "20px"
                }}
                sx={{ alignItems: "stretch" }}
              >
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_titulo}
                  sx={{ display: "flex", height: "100%" }}>
                  <TargetMin
                    icon={<SchoolIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                    value={freqTitulo.find((c) => c.id === "Educador/a de Párvulos")?.value || 0}
                    title="Educador/a de Párvulos"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_titulo}
                  sx={{ display: "flex", height: "100%" }}>
                  <TargetMin
                    icon={<SchoolIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                    value={freqTitulo.find((c) => c.id === "Profesor/a de Educación Básica")?.value || 0}
                    title="Profesor/a de Educación Básica"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_titulo}
                  sx={{ display: "flex", height: "100%" }}>
                  <TargetMin
                    icon={<SchoolIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                    value={freqTitulo.find((c) => c.id === "Profesor/a de Educación Media")?.value || 0}
                    title="Profesor/a de Educación Media"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_titulo}
                  sx={{ display: "flex", height: "100%" }}>
                  <TargetMin
                    icon={<SchoolIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                    value={freqTitulo.find((c) => c.id === "Otro")?.value || 0}
                    title="Otro Título"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
                <Clickable
                  to={DASHBOARD_ROUTES.formacion_titulo}
                  sx={{ display: "flex", height: "100%" }}>
                  <TargetMin
                    icon={<SchoolIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                    value={freqTitulo.find((c) => c.id === "—")?.value || 0}
                    title="Sin Información"
                    duration={1500}
                    fullWidth
                    variant="dash"
                    valueLabel={{
                      singular: "persona",
                      plural: "personas",
                    }}
                  />
                </Clickable>
              </Box>
              <Box
                mt={{
                  xs: 1.2,
                  md: 2
                }}>
                <Box
                  sx={{
                    height: 42,
                    display: "flex",
                    alignItems: "center",
                    mb: 0.5
                  }}>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={colors.primary[100]}
                    sx={{
                      m: 0,
                      fontFamily: "inherit"
                    }}
                  >
                    PROGRAMAS Y CARRERAS DONDE IMPARTEN CLASES
                  </Typography>
                </Box>
                <Box
                  display="grid"
                  gridTemplateColumns={{
                    xs: "repeat(1, 1fr)",
                    md: "repeat(3, 1fr)"
                  }}
                  gap={{
                    xs: "10px",
                    md: "20px"
                  }}
                  sx={{ alignItems: "stretch" }}
                >
                  <Clickable to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<ChildCareIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Pedagogía en Educación Parvularia")}
                      title="Pedagogía en Educación Parvularia"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<MenuBookIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Pedagogía en Educación Básica")}
                      title="Pedagogía en Educación Básica"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<SchoolIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Pedagogía en Educación Media")}
                      title="Pedagogía en Educación Media"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<Diversity3Icon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Pedagogía en Educación Diferencial")}
                      title="Pedagogía en Educación Diferencial"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable
                    to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<PsychologyIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Formación Pedagógica")}
                      title="Formación Pedagógica"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable
                    to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<WorkspacePremiumIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Postgrado")}
                      title="Postgrado"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable
                    to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<MiscellaneousServicesIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "Otros Programas")}
                      title="Otros Programas"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                  <Clickable
                    to={DASHBOARD_ROUTES.formacion_programas}
                    sx={{ display: "flex", height: "100%" }}>
                    <TargetMin
                      icon={<PsychologyIcon sx={{ color: colors.green[200], fontSize: 55 }} />}
                      value={getCount(programa, "—")}
                      title="Sin Información"
                      duration={1500}
                      fullWidth
                      variant="dash"
                      valueLabel={{
                        singular: "persona",
                        plural: "personas",
                      }}
                      sx={{ height: "100%" }}
                    />
                  </Clickable>
                </Box>
              </Box>
            </Box>
            {/* ==========================
              TRAYECTORIA + UNIVERSIDADES
           ========================== */}
            <Box
              sx={{
                gridColumn: {
                  xs: "span 12",
                  md: "span 7"
                },
                gridRow: "span 2",
                backgroundColor: colors.primary[200],
                pt: "15px",
                pr: "15px",
                pl: "0px",
                pb: "15px",
                display: "flex",
                flexDirection: "column",
                height: {
                  xs: 320,
                  md: 350
                },
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: 42,
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5
                }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={colors.primary[100]}
                  sx={{
                    lineHeight: 1,
                    m: 0,
                    fontFamily: "inherit"
                  }}
                >
                  EVOLUCIÓN RedFID
                </Typography>
              </Box>
              <Box
                flex="1"
                minHeight={0}
                overflow="visible">
                <Box
                  sx={{
                    height: {
                      xs: 230,
                      md: 260
                    }
                  }}>
                  <LineChart data={dataForLine} isDashboard={true} />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                gridColumn: {
                  xs: "span 12",
                  md: "span 5"
                },
                gridRow: "span 2",
                backgroundColor: colors.primary[200],
                pt: "15px",
                pr: "10px",
                pl: "0px",
                pb: "15px",
                display: "flex",
                flexDirection: "column",
                height: {
                  xs: 320,
                  md: 350
                },
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: 42,
                  display: "flex",
                  alignItems: "center",
                  mb: 1.5
                }}>
                <Typography
                  variant="h4"
                  fontWeight={600}
                  color={colors.primary[100]}
                  sx={{
                    lineHeight: 1,
                    m: 0,
                    fontFamily: "inherit"
                  }}
                >
                  UNIVERSIDADES
                </Typography>
              </Box>
              <Box
                flex="1"
                minHeight={0}
                overflow="auto"
                sx={{
                  pr: 1,
                  WebkitOverflowScrolling: "touch"
                }}>
                {universidades.map((uni, i) => {
                  const name = (uni.universidad_full).trim();
                  const imgSrc = universityImage(name);
                  return (
                    <Clickable
                      key={`${uni.id}-${i}`}
                      to={DASHBOARD_ROUTES.universidades}
                      style={{ textDecoration: "none" }}>
                      <TargetMin
                        fullWidth
                        title={name}
                        titleWrap
                        value={uni.formadores_qtty ?? 0}
                        valueLabel={{
                          singular: "persona",
                          plural: "personas",
                        }}
                        imgSrc={imgSrc}
                        mediaSize={35}
                        imgFit="contain"
                        imgRound={false}
                        bgColor="transparent"
                        shadow={false}
                        sx={{
                          py: 0,
                          px: 0,
                          mb: 0,
                          border: "none !important",
                          boxShadow: "none !important",
                          transform: "none !important",
                          borderRadius: 0,
                          "&:hover": {
                            boxShadow: "none !important",
                            transform: "none !important",
                          },
                          "& img": {
                            border: "none !important",
                            backgroundColor: "transparent !important",
                            padding: "0 !important",
                            boxShadow: "none !important",
                          },
                          "& .MuiBox-root": {
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                    </Clickable>
                  );
                })}
              </Box>

            </Box>
          </Box>

          {/* ==========================
            COLUMNA DERECHA (MAPA)
           ========================== */}
          {showMap && (
            <Box
              sx={{
                minWidth: 0,
                width: {
                  xs: "100%",
                  lg: 360
                },
                display: "flex",
                alignSelf: "start",
              }}
            >
              <Box
                backgroundColor={colors.primary[200]}
                pt="15px"
                pr="15px"
                pl="0px"
                pb="15px"
                display="flex"
                flexDirection="column"
                sx={{
                  width: "100%",
                  overflow: "visible",
                  height: {
                    xs: 1160,
                    sm: 1160,
                    md: 1160,
                    lg: 1160
                  },
                  minHeight: {
                    xs: 980,
                    sm: 980,
                    md: 980,
                    lg: 1160
                  },
                }}
              >
                <Box
                  sx={{
                    height: 42,
                    display: "flex",
                    alignItems: "center",
                    mb: 1.5
                  }}>
                  <Typography
                    variant="h4"
                    fontWeight={600}
                    color={colors.primary[100]}
                    sx={{
                      lineHeight: 1,
                      m: 0,
                      fontFamily: "inherit"
                    }}
                  >
                    REGIÓN
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    justifyContent: "center",
                    overflow: "visible",
                  }}
                >
                  <Box
                    sx={{
                      width: 300,
                      height: "100%",
                      flexShrink: 0,
                      overflow: "visible",

                      // border: "1px solid red",
                    }}
                  >
                    <Clickable
                      to={DASHBOARD_ROUTES.region}
                      sx={{ width: "100%", height: "100%" }}>
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%"
                        }}>
                        <GeographyChart isDashboard={true} data={freqRegion} />
                      </Box>
                    </Clickable>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </PageGuard>
  );
};

export default Dashboard;
